namespace $ {

	export type $shm_ba_agent_message_posts_spec = {
		version: 1
		posts: readonly Record< string, any >[]
	}

	export type $shm_ba_agent_message_posts_result = {
		spec: $shm_ba_agent_message_posts_spec | null
		table: $shm_ba_data_table | null
		error: string
	}

	const $shm_ba_agent_message_posts_head = [
		'Дата',
		'Заголовок',
		'Текст',
		'Источник',
		'Url',
		'Тип источника',
		'Тип сообщения',
		'Автор',
		'Url автора',
		'Теги',
		'Тональность',
		'Просмотров',
		'Комментариев',
		'Репостов',
		'Лайков',
		'Вовлеченность',
		'Аудитория',
		'Аудитория СМИ',
		'Цитируемость СМИ',
	] as const

	function $shm_ba_agent_message_posts_object( value: any ) {
		return Boolean( value ) && typeof value === 'object' && !Array.isArray( value )
	}

	function $shm_ba_agent_message_posts_text( value: any, limit = 20_000 ) {
		if( value === null || value === undefined ) return ''
		if( ![ 'string', 'number', 'boolean' ].includes( typeof value ) ) return ''
		return String( value ).slice( 0, limit )
	}

	function $shm_ba_agent_message_posts_title( value: any ) {
		return $shm_ba_agent_message_posts_text( value )
			.replace( /<\/?span\b[^>]*>/gi, '' )
			.replace( /<[^>]*>/g, '' )
	}

	function $shm_ba_agent_message_posts_url( value: any ) {
		const source = $shm_ba_agent_message_posts_text( value, 4_000 ).trim()
		if( !source ) return ''
		const parsed = $mol_error_fence(
			()=> new URL( source ),
			()=> null,
		)
		return parsed && [ 'http:', 'https:' ].includes( parsed.protocol ) ? parsed.toString() : ''
	}

	function $shm_ba_agent_message_posts_number( value: any ) {
		if( value === '' || value === null || value === undefined ) return ''
		const number = Number( value )
		return Number.isFinite( number ) ? number : ''
	}

	function $shm_ba_agent_message_posts_tags( value: any ) {
		if( !Array.isArray( value ) ) return ''
		return value
			.slice( 0, 100 )
			.map( item => $shm_ba_agent_message_posts_text( item, 500 ).trim() )
			.filter( Boolean )
			.join( ', ' )
	}

	function $shm_ba_agent_message_posts_row( post: Record< string, any > ) {
		const author = $shm_ba_agent_message_posts_object( post.author ) ? post.author : {}
		const metrics = $shm_ba_agent_message_posts_object( post.metrics ) ? post.metrics : {}
		return [
			$shm_ba_agent_message_posts_text( post.time, 200 ),
			$shm_ba_agent_message_posts_title( post.title ),
			$shm_ba_agent_message_posts_text( post.text ),
			$shm_ba_agent_message_posts_text( post.hub, 1_000 ),
			$shm_ba_agent_message_posts_url( post.url ),
			$shm_ba_agent_message_posts_text( post.source_type, 500 ),
			$shm_ba_agent_message_posts_text( post.type, 500 ),
			$shm_ba_agent_message_posts_text( author.name, 2_000 ),
			$shm_ba_agent_message_posts_url( author.url ),
			$shm_ba_agent_message_posts_tags( post.tags ),
			$shm_ba_agent_message_posts_text( post.tone, 500 ),
			$shm_ba_agent_message_posts_number( metrics.views ),
			$shm_ba_agent_message_posts_number( metrics.comments ),
			$shm_ba_agent_message_posts_number( metrics.reposts ),
			$shm_ba_agent_message_posts_number( metrics.likes ),
			$shm_ba_agent_message_posts_number( metrics.engagement ),
			$shm_ba_agent_message_posts_number( metrics.audience ?? author.audience ),
			$shm_ba_agent_message_posts_number( metrics.media_audience ),
			$shm_ba_agent_message_posts_number( metrics.citation_index ),
		] satisfies $shm_ba_data_cell[]
	}

	export function $shm_ba_agent_message_posts_parse( source: string ): $shm_ba_agent_message_posts_result {
		if( source.length > 1_000_000 ) return { spec: null, table: null, error: 'Описание постов слишком большое' }

		const decoded = $mol_error_fence(
			()=> ({ value: JSON.parse( source ), error: '' }),
			error => ({ value: null, error: error.message }),
		)
		if( decoded.error ) return { spec: null, table: null, error: `Некорректный JSON: ${ decoded.error }` }
		const parsed = decoded.value
		if( !$shm_ba_agent_message_posts_object( parsed ) ) {
			return { spec: null, table: null, error: 'Ожидался JSON-объект списка постов' }
		}
		if( parsed.version !== 1 ) return { spec: null, table: null, error: 'Поддерживается только version: 1' }
		if( !Array.isArray( parsed.posts ) ) return { spec: null, table: null, error: 'Поле posts должно содержать массив' }
		if( parsed.posts.length > 100 ) return { spec: null, table: null, error: 'В одном блоке может быть не больше 100 постов' }
		if( parsed.posts.some( ( post: any )=> !$shm_ba_agent_message_posts_object( post ) ) ) {
			return { spec: null, table: null, error: 'Каждый пост должен быть JSON-объектом' }
		}

		const posts = parsed.posts as Record< string, any >[]
		return {
			spec: { version: 1, posts },
			table: {
				head: [ ... $shm_ba_agent_message_posts_head ],
				rows: posts.map( $shm_ba_agent_message_posts_row ),
			},
			error: '',
		}
	}

	export function $shm_ba_agent_message_posts_instruction() {
		return [
			'Когда показываешь пользователю найденные сообщения или посты Brand Analytics, обязательно выводи их отдельным fenced-блоком ba-posts.',
			'Перед блоком коротко объясни, что найдено. Внутри блока передавай нативные объекты BA без переименования полей строго в формате:',
			'```ba-posts',
			'{"version":1,"posts":[{"hash":"...","time":"2026-08-18 10:18","hub":"2gis.ru","url":"https://...","type":"comment","author":{"name":"Автор","url":"https://...","audience":0},"text":"Текст","tone":"neutral","tags":["Тег"],"metrics":{"comments":0,"reposts":0,"likes":0,"views":0,"audience":0,"engagement":0,"er":0},"geo":{"country":"Россия","city":"Москва"},"title":"Заголовок","classify":{},"duplicate_count":1}]}',
			'```',
			'Не оформляй массив сообщений BA обычным JSON-кодом, Markdown-таблицей или списком. Не помещай поясняющий текст внутрь ba-posts-блока.',
			'Для агрегатов и динамики используй echarts, а ba-posts используй именно для отдельных сообщений.',
		].join( '\n' )
	}

}
